import { Theme, FormLabel, FormGroup, FormControlLabel, Grid, Checkbox } from '@material-ui/core';
import React, { useContext, FC, useEffect, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/styles';

import { MobileFieldControlDefinition, parseFieldIdentifier } from './form/services';
import { FormControlProps, useDataFieldValidity, useDataValue, useEditability, useExpressionValue } from './form/utils';
import { FormControl } from './form/components/FormControl';
import { FormContext } from './form/FormContext';
import { TextField } from './form/components/TextField';

export interface MultiCheckboxControlDefinition extends MobileFieldControlDefinition<'MultiCheckbox'> {
  options: string[];
  NAOption?: string;
  optionInputs?: {value: string, dataPath: string}[];
}

export interface MultiCheckboxProps extends FormControlProps<MultiCheckboxControlDefinition> {
}

const useStyles = makeStyles(function(theme: Theme) {
  return createStyles( {
    root: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column'
    },
    error: {
      color: theme.palette.error.main,
      marginTop: theme.spacing(-3),
      marginLeft: theme.spacing(3)
    }
  })
});

function multiToggle(currentSelection: string[], selected: string, NAOption = 'None of the above') {
  let newSelection;

  if (currentSelection?.includes(selected)) {
    newSelection = currentSelection.filter(s => s !== selected);
  } else if (NAOption && selected === NAOption) {
    newSelection = [selected];
  } else if (NAOption && currentSelection?.includes(NAOption)) {
    newSelection = [selected];
  } else if (currentSelection === null || currentSelection.indexOf(selected) < 0) {
    // Toggle on
    newSelection = [...(currentSelection === null ? [] : currentSelection), selected ];
  } else {
    // Toggle off
    newSelection = [...currentSelection];
    newSelection.splice(currentSelection.indexOf(selected), 1);
    if (newSelection.length === 0)
      newSelection = null;
  }
  return newSelection;
}

/**
 * This also manages an "Other" field and a "None of the above" field, if either are specified.
 * 
 * * Clicking the provided "None of the above" field will uncheck all other options, and clicking any option other than the
 * * "None of the above" option will uncheck "None of the above"
 * * If an "Other" field is present, it will be cleared if "Other" is unchecked or if "None of the above" is checked.
 *   The "Other" field is only editable if "Other" is checked.
 */
export const MultiCheckbox: FC<MultiCheckboxProps> = function(props) {
  const definition = props.definition;
  const NAOption = definition.NAOption || 'None of the above';
  const optionInputs = definition.optionInputs || [];
  const classes = useStyles(props);
  const { onSetData, onRemoveData, scope } = useContext(FormContext);
  const error = useDataFieldValidity(definition.dataPath);

  const selection = useDataValue<string | string[]>(definition.dataPath) || null;
  const options = useExpressionValue<string[]>(definition.options)!;
  const [otherDataPath, setOtherDataPath] = useState('');
  const editable = useEditability(definition.editable);

  useEffect(() => {
    if (!selection) {
      return;
    }

    if (!selection.length) {
      onRemoveData(definition.dataPath, scope);
    }
  }, [selection])

  useEffect(() => {
    setOtherDataPath('{' + parseFieldIdentifier(definition.dataPath) + 'Other}');
  }, [definition.dataPath])

  const handleSelection = (selected: string | string[], scope: string) => {

    const newSelection = multiToggle(selection as string[], selected as string, NAOption);
    
    if (selected === NAOption && newSelection?.includes(NAOption)) {
      onRemoveData(otherDataPath, scope);
    }
    if (selected === 'Other' && newSelection && !newSelection.includes('Other')) {
      onRemoveData(otherDataPath, scope);
    }

    if (optionInputs) {
      let index = optionInputs.findIndex((optionInput) => selected.toString() === optionInput.value);
      if (index > -1 && newSelection && !newSelection.includes(selected.toString())) {
        onRemoveData(optionInputs[index].dataPath, scope);
      }
    }
    if (newSelection === null || newSelection.length === 0) {
      onRemoveData(definition.dataPath, scope);
    } else {
      onSetData(definition.dataPath, scope, newSelection);
    }
  };

  return (
    <div className = { classes.root }>
      <FormControl
        label = { definition.label }
        helpText = { definition.helpText }
        footnote = { definition.footnote }
        style = { definition.style }
        error = { error }>
        <FormLabel></FormLabel>
          <FormGroup>
          {
            options.map((option, i) => {
              const isSelected = selection !== null &&
                (Array.isArray(selection)
                  ? selection.indexOf(option) >= 0
                  : selection === option)
              const index = optionInputs.findIndex((optionInput) => optionInput.value === option)

              return <React.Fragment key={i}>
                {
                  option === 'Other'
                    ? <Grid container direction="row" wrap="nowrap" spacing={1}>
                        <Grid container item xl={1} lg={1} md={2} sm={3} xs={2}>
                          <FormControlLabel
                            control={
                              <Checkbox checked={isSelected} onChange={ () => handleSelection(option, scope) } name={option} disabled={!editable} />
                            }
                            label={option}
                          />
                        </Grid>
                        <Grid container item>
                          { otherDataPath && <TextField path={ otherDataPath } definition={{editable: isSelected, type: 'TextField', label: '', helpText: '', dataPath: otherDataPath, debounceTime: 300 } as any}/> }
                        </Grid>
                      </Grid>
                    : <Grid container direction="row" wrap="nowrap" alignContent="flex-start">
                        <Grid container item style={optionInputs ? { "flex": "0 1 auto", "minWidth": "15%", "maxWidth": "50%", "width": "auto" } : {}}>
                          <FormControlLabel
                            control={
                              <Checkbox checked={isSelected} onChange={ () => handleSelection(option, scope) } name={option} disabled={!editable} />
                            }
                            label={option}
                          />
                        </Grid>
                        {
                          index > -1
                          ?
                            <Grid container item style={{ "flex": "1 1 auto", "minWidth": "50%", "width": "auto" }}>
                              { optionInputs[index] && <TextField path={ optionInputs[index].dataPath } definition={{editable: isSelected, type: 'TextField', label: '', helpText: '', dataPath: optionInputs[index].dataPath, debounceTime: 300 } as any}/> }
                            </Grid>
                          :
                            <></>
                        }
                      </Grid>
                }
                </React.Fragment>
            })
          }
        </FormGroup>
      </FormControl>
    </div>
  )
};
